/**
 * video-editor/preview.js — проигрыватель ленты в превью.
 *
 * Кадры — из обычных <video>: браузер синхронизирует картинку и звук лучше
 * любых самописных часов, и перемотка элемента дешевле, чем поднимать
 * декодер на каждое движение ползунка (так же решено у /slideshow-maker,
 * slideshow-clip-source.js). Рисует кадр НЕ этот модуль, а
 * VideoEditorCompose.draw — тот же вызов, что у экспорта.
 *
 * Звук превью — звук самих элементов (клипов и дорожек), громкость — по
 * огибающим модели (`clipGainAt`, `itemGainAt`), тем же числам, по которым
 * движок рендерит файл. Громкость идёт через Web Audio (GainNode), а не через
 * `el.volume`: на iOS `volume` только для чтения (всегда 1), и затухания в
 * превью там просто не было бы; заодно работает усиление выше 100 %.
 *
 * ⚠️ Часы. Пока играет ОДИН видеоклип, время ленты берётся из его
 * элемента (он и держит звук с картинкой вместе); в остальное время —
 * из performance.now(). Элемент, ушедший от нужной секунды дальше чем на
 * DRIFT, перематывается.
 *
 * window.VideoEditorPreview.create({ canvas, media, afterDraw, onTick, onEnd })
 *   .setModel(tl, regions) · .setSize(W, H) · .render(t) · .play(t) · .pause()
 *   .isPlaying() · .time() · .audioCtx() · .unlock() · .dispose()
 */
(function () {
'use strict';
if (window.VideoEditorPreview) return;

var DRIFT = 0.25;          // с — дальше этого элемент перематывается
var PREROLL = 0.8;         // с — за столько до начала клипа его элемент встаёт на место
/* Музыка и голос: у них нет картинки, по которой видно отставание, а слышно
   его уже с 0.1–0.15 с (голос под губами). Перемотка аудио-элемента дешёвая. */
var AUDIO_DRIFT = 0.12;

function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

function create(opts) {
    var canvas = opts.canvas, ctx = canvas.getContext('2d');
    var media = opts.media;
    var tl = null, regions = [];
    var W = canvas.width, H = canvas.height;
    var playhead = 0, playing = false, raf = 0;
    var clockT0 = 0, wall0 = 0;
    var gen = 0;
    var slots = {};            // mediaId → [el0, el1]
    var audioEls = {};         // itemId → <audio>
    var ovEls = {};            // id видео-наложения → свой <video>
    var actx = null;

    /* ── элементы ─────────────────────────────────────────────────────── */
    function slot(rec, i) {
        var arr = slots[rec.id] || (slots[rec.id] = []);
        if (arr[i]) return arr[i];
        var el = (i === 0 && rec.el) ? rec.el : media.makeVideoEl(rec.url);
        el.muted = true;
        arr[i] = el;
        return el;
    }
    /**
     * Источник кадра для записи frameAt(); `avoid` — элемент соседа по переходу.
     * ⚠️ Клип остаётся на ТОМ элементе, который его уже играет, а новый клип
     * берёт элемент, заранее поставленный на его первую секунду (PREROLL).
     * Раньше всегда брался элемент 0: на стыке двух кусков ОДНОГО файла он
     * уезжал перематываться на следующий кусок, а поставленный заранее
     * элемент 1 не брался вовсе — кадр стыка выходил чёрным (разбор 30.09).
     */
    function srcFor(entry, avoid) {
        if (!entry) return null;
        var rec = media.get(entry.clip.mediaId);
        if (!rec || rec.state !== 'ready') return null;
        if (entry.clip.kind === 'image') return rec.bitmap || null;
        if (entry.clip.kind !== 'video') return null;          // титульный кадр рисует компоновщик
        var id = entry.clip.id, arr = slots[rec.id] || [];
        for (var i = 0; i < arr.length; i++) if (arr[i] && arr[i] !== avoid && arr[i]._veClip === id) return arr[i];
        for (var j = 0; j < arr.length; j++) if (arr[j] && arr[j] !== avoid && arr[j]._vePre === id) return arr[j];
        var el = slot(rec, 0);
        if (el === avoid || (playing && el._veClip && el._veClip !== id && !el.paused)) el = slot(rec, 1);
        return el;
    }
    function drawable(src) {
        if (!src) return null;
        if (src.tagName === 'VIDEO') return src.readyState >= 2 ? src : null;
        return src;
    }

    /* ── видео поверх видео ───────────────────────────────────────────────
       У каждого видео-наложения СВОЙ <video>: тот же файл может в это же
       время играть клипом ленты, и секунды у них разные. Звук — через тот же
       GainNode по огибающей модели (ovGainAt), как у клипов. */
    function ovElFor(o) {
        var rec = media.get(o.mediaId);
        if (!rec || rec.state !== 'ready' || !rec.url) return null;
        var el = ovEls[o.id];
        if (el && el._veSrc === rec.url) return el;
        if (el) dropOvEl(o.id);
        el = media.makeVideoEl(rec.url);
        el._veSrc = rec.url;
        el.muted = true;
        ovEls[o.id] = el;
        return el;
    }
    function dropOvEl(id) {
        var el = ovEls[id];
        if (!el) return;
        try { el.pause(); el.removeAttribute('src'); el.load(); el.remove(); } catch (e) {}
        delete ovEls[id];
    }
    /** Видео-наложения, видимые в момент t: { o, j, at — секунда источника }. */
    function ovActive(t) {
        var out = [];
        if (!tl || !tl.overlays) return out;
        for (var j = 0; j < tl.overlays.length; j++) {
            var o = tl.overlays[j];
            if (o.kind !== 'video') continue;
            var at = tl.ovSourceAt(j, t);
            if (at != null) out.push({ o: o, j: j, at: at });
        }
        return out;
    }
    /** Кадры видео-наложений для рисовальщика (ещё не готовый элемент — без кадра). */
    function ovSources(t) {
        var map = null;
        ovActive(t).forEach(function (a) {
            var el = ovEls[a.o.id];
            if (el && el.readyState >= 2) { map = map || {}; map[a.o.id] = el; }
        });
        return map;
    }

    /* Перемотка с объединением: пока элемент ищет одну секунду, новые просьбы
       только обновляют цель, и после приземления он доезжает до последней. */
    function seekEl(v, t) {
        v._veWant = t;
        if (v._veSeekP) return v._veSeekP;
        var p = (async function () {
            for (var guard = 0; guard < 6; guard++) {
                var want = v._veWant;
                if (Math.abs(v.currentTime - want) < 0.015 && v.readyState >= 2) break;
                if (typeof window.__seekPresented === 'function') await window.__seekPresented(v, want, 2500);
                else { try { v.currentTime = want; } catch (e) {} await new Promise(function (r) { setTimeout(r, 120); }); }
                if (Math.abs(v._veWant - want) < 1e-3) break;
            }
        })();
        /* ⚠️ Снимать метку «ищет» — ПОСЛЕ присваивания, а не внутри функции:
           элемент, уже стоящий на нужной секунде, выходит из цикла без единого
           await, и присваивание снаружи случилось бы ПОСЛЕ обнуления внутри —
           метка «ищет» оставалась бы навсегда, и превью больше не
           перематывалось бы вовсе (стенд [4b], 30.09: экран стоял на первом
           кадре, файл шёл дальше). */
        v._veSeekP = p;
        p.then(function () { if (v._veSeekP === p) v._veSeekP = null; }, function () { if (v._veSeekP === p) v._veSeekP = null; });
        return p;
    }

    /* ── звук: GainNode на элемент ────────────────────────────────────── */
    function ensureCtx() {
        if (actx) return actx;
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        try {
            actx = (typeof window.__liveAudioCtx === 'function')
                ? window.__liveAudioCtx(null, function () { return new AC(); })
                : new AC();
        } catch (e) { actx = null; }
        return actx;
    }
    function route(el) {
        if (el._veGain || el._veNoGraph) return;
        var c = ensureCtx();
        if (!c) { el._veNoGraph = true; return; }
        try {
            var src = c.createMediaElementSource(el);
            var g = c.createGain();
            g.gain.value = 0;
            src.connect(g); g.connect(c.destination);
            el._veGain = g;
            el.volume = 1;
        } catch (e) { el._veNoGraph = true; }
    }
    function setGain(el, g) {
        g = Math.max(0, g || 0);
        if (el._veGain && actx) {
            el.muted = false;
            try { el._veGain.gain.setTargetAtTime(g, actx.currentTime, 0.012); } catch (e) { el._veGain.gain.value = g; }
        } else {
            el.muted = g <= 0.0005;
            try { el.volume = clamp(g, 0, 1); } catch (e) {}
        }
    }
    function silence(el) {
        if (el._veGain) { try { el._veGain.gain.value = 0; } catch (e) {} }
        el.muted = true;
    }

    /* ── кадр ──────────────────────────────────────────────────────────── */
    function drawAt(t, fr, sa, sb) {
        var C = window.VideoEditorCompose;
        if (!C) return;
        C.draw(ctx, W, H, t, tl, fr, drawable(sa), drawable(sb), regions, ovSources(t));
        if (opts.afterDraw) { try { opts.afterDraw(ctx, W, H, t); } catch (e) {} }
    }

    function ready(src) { return !src || src.tagName !== 'VIDEO' || (src.readyState >= 2 && !src.seeking); }

    /** Кадр на паузе: рисуем сразу тем, что есть, и ещё раз — когда перемотка приземлится. */
    function render(t) {
        playhead = t;
        if (playing || !tl) return;
        var my = ++gen;
        var fr = tl.frameAt(t);
        var sa = fr ? srcFor(fr.a, null) : null;
        var sb = fr && fr.b ? srcFor(fr.b, sa) : null;
        /* Элемент, который ещё ищет кадр, нарисовался бы ЧЁРНЫМ: при протяжке
           ползунка экран мигал бы. Пока ищет — на экране остаётся прежний кадр. */
        var ovs = ovActive(t).map(function (a) {
            var el = ovElFor(a.o);
            return el ? { el: el, at: a.at } : null;
        }).filter(Boolean);
        var ovReady = ovs.every(function (x) { return ready(x.el); });
        if (ready(sa) && ready(sb) && ovReady) drawAt(t, fr, sa, sb);
        var waits = [];
        if (sa && sa.tagName === 'VIDEO') waits.push(seekEl(sa, fr.a.sourceSec));
        if (sb && sb.tagName === 'VIDEO') waits.push(seekEl(sb, fr.b.sourceSec));
        ovs.forEach(function (x) { waits.push(seekEl(x.el, x.at)); });
        if (waits.length) {
            pendingDraw = my;
            Promise.all(waits).then(function () { drawWhenReady(my, sa, sb, 0, ovs); });
        }
    }
    /* Перемотка «приземлилась» ещё не значит «кадр есть»: под нагрузкой
       элемент какое-то время стоит на readyState 1, и кадр, нарисованный в эту
       минуту, вышел бы чёрным. Ждём кадр (до 2 с), потом рисуем в любом случае. */
    var pendingDraw = 0;
    function drawWhenReady(my, sa, sb, n, ovs) {
        if (my !== gen || playing) { if (pendingDraw === my) pendingDraw = 0; return; }
        var ovOk = (ovs || []).every(function (x) { return ready(x.el); });
        if ((ready(sa) && ready(sb) && ovOk) || n >= 20) {
            if (pendingDraw === my) pendingDraw = 0;
            drawAt(playhead, tl.frameAt(playhead), sa, sb);
            return;
        }
        setTimeout(function () { drawWhenReady(my, sa, sb, n + 1, ovs); }, 100);
    }

    /* ── воспроизведение ──────────────────────────────────────────────── */

    /** Разблокировать звук ВНУТРИ жеста (iOS: элемент без жеста не заиграет со звуком). */
    function unlock() {
        var c = ensureCtx();
        if (c && c.state !== 'running') { try { c.resume(); } catch (e) {} }
    }

    function play(from) {
        if (!tl || !(tl.duration > 0)) return;
        unlock();
        playhead = (from != null) ? from : playhead;
        if (playhead >= tl.duration - 0.03) playhead = 0;
        playing = true;
        clockT0 = playhead; wall0 = performance.now();
        gen++;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
    }

    function pause() {
        playing = false;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        eachEl(function (el) { try { el.pause(); } catch (e) {} silence(el); });
    }

    function eachEl(fn) {
        Object.keys(slots).forEach(function (k) { slots[k].forEach(function (el) { if (el) fn(el); }); });
        Object.keys(audioEls).forEach(function (k) { fn(audioEls[k]); });
        Object.keys(ovEls).forEach(function (k) { fn(ovEls[k]); });
    }

    function startEl(el, at, rate) {
        try { if (Math.abs(el.currentTime - at) > 0.03) el.currentTime = at; } catch (e) {}
        try { el.playbackRate = clamp(rate || 1, 0.0625, 16); } catch (e) {}
        var p = el.play();
        if (p && p.catch) p.catch(function () {
            // Запрет автозапуска со звуком — играем молча, картинка важнее.
            el.muted = true; el.play().catch(function () {});
        });
    }

    function audioElFor(item) {
        var el = audioEls[item.id];
        var rec = media.get(item.mediaId);
        if (!rec || rec.state !== 'ready' || !rec.url) return null;
        if (el && el._veSrc === rec.url) return el;
        if (el) { try { el.pause(); el.remove(); } catch (e) {} }
        el = document.createElement('audio');
        el.preload = 'auto';
        el._veSrc = rec.url;
        el.src = rec.url;
        el.muted = true;
        var host = rec.el && rec.el.parentNode;
        if (host) host.appendChild(el);
        audioEls[item.id] = el;
        return el;
    }

    function tick(now) {
        if (!playing) return;
        var t = clockT0 + (now - wall0) / 1000;
        var total = tl.duration;
        if (t >= total) {
            playhead = total;
            pause();
            render(total);
            if (opts.onTick) opts.onTick(total);
            if (opts.onEnd) opts.onEnd();
            return;
        }
        var fr = tl.frameAt(t);
        var used = [];
        var entries = fr ? (fr.b ? [fr.a, fr.b] : [fr.a]) : [];
        var srcs = [null, null];
        var leader = null;
        entries.forEach(function (en, i) {
            var s = srcFor(en, i ? srcs[0] : null);
            srcs[i] = s;
            if (!s || s.tagName !== 'VIDEO') return;
            used.push(s);
            route(s);
            var want = en.sourceSec;
            if (s.paused || s._veClip !== en.clip.id) {
                s._veClip = en.clip.id;
                s._vePre = null;
                startEl(s, want, en.clip.speed);
            } else if (!s.seeking && Math.abs(s.currentTime - want) > DRIFT) {
                try { s.currentTime = want; } catch (e) {}
            }
            try { if (Math.abs(s.playbackRate - en.clip.speed) > 1e-3) s.playbackRate = clamp(en.clip.speed, 0.0625, 16); } catch (e) {}
            setGain(s, tl.clipGainAt(en.index, t));
            if (!fr.b) leader = { el: s, en: en };
        });
        // Часы от ведущего элемента: он держит звук и картинку вместе.
        if (leader && !leader.el.paused && !leader.el.seeking && leader.el.readyState >= 3) {
            var c = leader.en.clip;
            var derived = tl.starts[leader.en.index] + (leader.el.currentTime - c.trimStart) / c.speed;
            if (Math.abs(derived - t) > 0.04 && Math.abs(derived - t) < 1.0) {
                t = derived; clockT0 = t; wall0 = now;
            }
        }
        // Следующий клип заранее встаёт на свою первую секунду.
        for (var k = 0; k < tl.clips.length; k++) {
            var st = tl.starts[k];
            if (st > t && st - t < PREROLL && tl.clips[k].kind === 'video') {
                var rec = media.get(tl.clips[k].mediaId);
                if (!rec || rec.state !== 'ready') continue;
                var el = slot(rec, 0);
                if (used.indexOf(el) >= 0) el = slot(rec, 1);
                if (used.indexOf(el) < 0 && el.paused && !el.seeking && Math.abs(el.currentTime - tl.clips[k].trimStart) > 0.05) {
                    try { el.currentTime = tl.clips[k].trimStart; } catch (e) {}
                }
                // Метка «этот элемент ждёт клип k»: на стыке его и возьмёт srcFor.
                if (used.indexOf(el) < 0) { el._vePre = tl.clips[k].id; used.push(el); }
            }
        }
        Object.keys(slots).forEach(function (key) {
            slots[key].forEach(function (el) {
                if (el && used.indexOf(el) < 0 && !el.paused) { try { el.pause(); } catch (e) {} silence(el); el._veClip = null; }
            });
        });
        // Звуковые дорожки.
        for (var j = 0; j < tl.audio.length; j++) {
            var it = tl.audio[j];
            var ael = audioElFor(it);
            if (!ael) continue;
            var at = tl.itemSourceAt(j, t);
            if (at == null) { if (!ael.paused) { try { ael.pause(); } catch (e) {} silence(ael); } continue; }
            route(ael);
            if (ael.paused) startEl(ael, at, 1);
            else if (!ael.seeking && Math.abs(ael.currentTime - at) > AUDIO_DRIFT) { try { ael.currentTime = at; } catch (e) {} }
            setGain(ael, tl.itemGainAt(j, t));
        }
        Object.keys(audioEls).forEach(function (id) {
            var still = tl.audio.some(function (a) { return a.id === id; });
            if (!still) { try { audioEls[id].pause(); audioEls[id].remove(); } catch (e) {} delete audioEls[id]; }
        });
        // Видео поверх видео: своя секунда, своя громкость, заранее — на место.
        var ovUsed = {}, ovPlaying = [];
        for (var q = 0; q < tl.overlays.length; q++) {
            var o = tl.overlays[q];
            if (o.kind !== 'video') continue;
            var oat = tl.ovSourceAt(q, t);
            if (oat == null) {
                if (o.start > t && o.start - t < PREROLL) {
                    var pe = ovElFor(o);
                    if (pe && pe.paused && !pe.seeking && Math.abs(pe.currentTime - o.trimStart) > 0.05) { try { pe.currentTime = o.trimStart; } catch (e) {} }
                    if (pe) ovUsed[o.id] = 1;
                }
                continue;
            }
            var oel = ovElFor(o);
            if (!oel) continue;
            ovUsed[o.id] = 1;
            ovPlaying.push(oel);
            route(oel);
            if (oel.paused) startEl(oel, oat, 1);
            else if (!oel.seeking && Math.abs(oel.currentTime - oat) > DRIFT) { try { oel.currentTime = oat; } catch (e) {} }
            setGain(oel, tl.ovGainAt(q, t));
        }
        Object.keys(ovEls).forEach(function (id) {
            if (!tl.overlays.some(function (x) { return x.id === id && x.kind === 'video'; })) { dropOvEl(id); return; }
            if (!ovUsed[id] && !ovEls[id].paused) { try { ovEls[id].pause(); } catch (e) {} silence(ovEls[id]); }
        });
        playhead = t;
        /* Элемент, который ещё ищет кадр (стык, перемотка), нарисовался бы
           ЧЁРНЫМ — на экране остаётся прошлый кадр, но не дольше HOLD_MS:
           сломанный источник не должен заморозить превью навсегда. */
        var ok = srcs.concat(ovPlaying).every(function (sx) { return !sx || sx.tagName !== 'VIDEO' || (sx.readyState >= 2 && !sx.seeking); });
        if (ok || !drewOnce || (holdSince && now - holdSince > HOLD_MS)) {
            drawAt(t, fr, srcs[0], srcs[1]);
            drewOnce = true; holdSince = 0;
        } else if (!holdSince) holdSince = now;
        if (opts.onTick) opts.onTick(t);
        raf = requestAnimationFrame(tick);
    }
    var drewOnce = false, holdSince = 0, HOLD_MS = 700;

    /** Перемотка во время воспроизведения: часы — на новое место, элементы догонят сами. */
    function seek(t) {
        if (!tl) return;
        t = clamp(t, 0, tl.duration);
        if (!playing) { render(t); return; }
        playhead = t; clockT0 = t; wall0 = performance.now();
    }

    function setModel(model, regs) {
        tl = model; regions = regs || [];
    }
    function setSize(w, h) {
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
        W = w; H = h;
    }

    // Скрытая вкладка: rAF стоит, а элементы играли бы звук дальше вслепую.
    document.addEventListener('visibilitychange', function () { if (document.hidden && playing) { pause(); if (opts.onPauseByHide) opts.onPauseByHide(); } });

    return {
        setModel: setModel, setSize: setSize, render: render, play: play, pause: pause, unlock: unlock, seek: seek,
        isPlaying: function () { return playing; },
        /** Ни один элемент превью не ищет кадр — экран показывает момент ползунка. */
        settled: function () {
            var busy = !!pendingDraw;
            Object.keys(slots).forEach(function (k) { slots[k].forEach(function (el) { if (el && (el._veSeekP || el.seeking)) busy = true; }); });
            Object.keys(ovEls).forEach(function (k) { var el = ovEls[k]; if (el && (el._veSeekP || el.seeking)) busy = true; });
            return !busy;
        },
        time: function () { return playhead; },
        /** Контекст, через который звучит превью: запись голоса слушает его часы. */
        audioCtx: function () { return ensureCtx(); },
        size: function () { return { w: W, h: H }; },
        dispose: function () {
            pause();
            Object.keys(slots).forEach(function (k) {
                slots[k].forEach(function (el, i) { if (el && i > 0) { try { el.removeAttribute('src'); el.load(); el.remove(); } catch (e) {} } });
            });
            slots = {};
            Object.keys(audioEls).forEach(function (k) { try { audioEls[k].pause(); audioEls[k].remove(); } catch (e) {} });
            audioEls = {};
            Object.keys(ovEls).forEach(dropOvEl);
        },
    };
}

window.VideoEditorPreview = { create: create };
})();
